/**
 * Scroll math for anchoring the newest comment in the record Comments tab
 * (operator-task-fe-comments-tab-scroll-overshoot-and-post-scroll).
 *
 * The Comments panel is NOT its own scroll container — the composer is pinned
 * `position: sticky; bottom: 0` inside the single record scroll view (`.main`,
 * overflow:auto), and that same view carries a sticky top header: the
 * always-sticky `.state-tabs` tab bar (and, when scroll-pinned, the
 * `.page-header-sticky` breadcrumb/title header above it). So a naive
 * `scrollIntoView({ block: "nearest" | "end" })` on the
 * newest comment overshoots: it aligns the comment against the raw container
 * edges, ignoring that the sticky header occludes the top and the sticky
 * composer occludes the bottom. The result is the reported bug — the last
 * comment's first lines scrolled up behind the header, or the just-posted
 * comment hidden behind the composer.
 *
 * `computeCommentScrollDelta` returns the signed scroll delta that seats the
 * newest comment's BOTTOM just above the composer (so it reads right above
 * where you type), falling back to aligning its TOP just below the header when
 * the comment is taller than the available band — so the first line is never
 * clipped (no overshoot). Kept as a pure function of measured geometry so it is
 * unit-testable without a live layout.
 */
export interface CommentScrollGeometry {
    /** Newest comment element's viewport-space top edge (px). */
    elTop: number;
    /** Newest comment element's viewport-space bottom edge (px). */
    elBottom: number;
    /** Scroll container's viewport-space top edge (px). */
    scTop: number;
    /** Scroll container's viewport-space bottom edge (px). */
    scBottom: number;
    /** Sticky composer footer height — the bottom occlusion (px). */
    composerHeight: number;
    /**
     * Viewport-space bottom edge of the sticky top header — the top occlusion.
     * Pass the scroll container's top when there is no sticky header.
     */
    headerBottom: number;
    /** Breathing room kept between the comment and the header / composer (px). */
    gap: number;
}

/**
 * Signed scroll delta (px; positive = scroll the container down) so the newest
 * comment is fully readable between the sticky header and the sticky composer.
 *
 * Preference order:
 *  1. Seat the comment's bottom `gap` px above the composer (newest comment sits
 *     right above the composer — the natural "jump to latest" resting place).
 *  2. If that would push the comment's first line up behind the sticky header
 *     (i.e. the comment is taller than the visible band), align its top `gap` px
 *     below the header instead, so the first line stays visible and the bottom
 *     runs behind the composer (the user can scroll down for the rest).
 */
export const computeCommentScrollDelta = (g: CommentScrollGeometry): number => {
    const topLimit = Math.max(g.scTop, g.headerBottom) + g.gap;
    const bottomLimit = g.scBottom - g.composerHeight - g.gap;
    // Scroll so the comment bottom lands on bottomLimit.
    let delta = g.elBottom - bottomLimit;
    // getBoundingClientRect is measured pre-scroll; scrolling down by `delta`
    // moves content up by `delta`, so the comment's projected top is elTop-delta.
    if (g.elTop - delta < topLimit) {
        // Aligning the bottom would clip the first line behind the header —
        // align the top just below the header instead.
        delta = g.elTop - topLimit;
    }
    return delta;
};

/**
 * Signed scroll delta (px; positive = scroll the container down) that aligns the
 * target comment's TOP just below the sticky header (`headerBottom + gap`).
 *
 * Unlike `computeCommentScrollDelta` — which seats the NEWEST comment's BOTTOM
 * just above the composer (the "jump to latest" resting place) — this anchors an
 * arbitrary comment at the TOP of the readable band so the reader reads DOWNWARD
 * from it. Used when the Comments tab opens on the FIRST UNREAD comment: the
 * reader resumes at the oldest-unread and scrolls down through the rest, rather
 * than the unread comment being seated at the bottom with the newer unread ones
 * hidden below the composer (operator-task-fe-comments-tab-scroll-to-first-unread).
 */
export const computeCommentTopAlignDelta = (g: CommentScrollGeometry): number => {
    const topLimit = Math.max(g.scTop, g.headerBottom) + g.gap;
    return g.elTop - topLimit;
};

/**
 * Whether an element is already fully inside the readable band — below the sticky
 * top header and above the sticky bottom composer — and therefore needs NO
 * scroll. Unlike `computeCommentScrollDelta` this is a pure OCCLUSION test with
 * no breathing-room gap, so a caller can keep the scroll position stable when the
 * target is already visible and only scroll it into view when it is genuinely
 * occluded. Used when opening a comment's in-place edit box so the Save button is
 * scrolled into view only if it would otherwise be hidden behind the composer /
 * below the fold — the "keep scroll stable if possible, else scroll Save into
 * view" behaviour (operator-task-fe-comment-edit-scroll-save-button-hidden).
 */
export const isFullyWithinBand = (g: CommentScrollGeometry): boolean => {
    const topLimit = Math.max(g.scTop, g.headerBottom);
    const bottomLimit = g.scBottom - g.composerHeight;
    return g.elTop >= topLimit && g.elBottom <= bottomLimit;
};

/**
 * Nearest scrollable ancestor of `node` (the element that actually scrolls the
 * comment list — the record `.main` view), or null when none is found. Robust
 * to the exact class name: it matches any ancestor whose computed `overflow-y`
 * is auto/scroll/overlay AND that is genuinely overflowing.
 */
export const getScrollParent = (node: HTMLElement | null): HTMLElement | null => {
    let cur: HTMLElement | null = node?.parentElement ?? null;
    while (cur) {
        const overflowY = window.getComputedStyle(cur).overflowY;
        if (
            (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
            cur.scrollHeight > cur.clientHeight
        ) {
            return cur;
        }
        cur = cur.parentElement;
    }
    return null;
};
